let matrixClient = null;
const recoveryPassphrases = new Map();
const LEGACY_RECOVERY_PASSPHRASE_KEY = "key_passphrase";
const ROOM_READY_ATTEMPT_LIMIT = 10;
const ROOM_READY_RETRY_DELAY_MS = 400;

if (typeof localStorage !== "undefined") {
    localStorage.removeItem(LEGACY_RECOVERY_PASSPHRASE_KEY);
}

const getClientSessionKey = (client) => {
    const userId = client?.getUserId?.();
    const deviceId = client?.getDeviceId?.();
    return userId && deviceId ? `${userId}:${deviceId}` : null;
};
  
export function setMatrixClientSession(client) {
    matrixClient = client;
}

export function getMatrixClientSession() {
    return matrixClient;
}

export function clearMatrixClientSession() {
    matrixClient = null;
}

export const SecurePassphraseStorage = {
    set(passphrase, client) {
        const sessionKey = getClientSessionKey(client);
        if (sessionKey) {
            recoveryPassphrases.set(sessionKey, passphrase);
        }
    },
    get(client) {
        const sessionKey = getClientSessionKey(client);
        return sessionKey ? recoveryPassphrases.get(sessionKey) || null : null;
    },
    clear(client) {
        const sessionKey = getClientSessionKey(client);
        if (sessionKey) {
            recoveryPassphrases.delete(sessionKey);
        }
    },
};

/**
 * Stops the Matrix client while preserving its installation binding and crypto store.
 * The proxy token remains valid across ordinary Me logout/login.
 */
export async function fullMatrixLogout() {
    const client = getMatrixClientSession();

    try {
        if (client) {
            SecurePassphraseStorage.clear(client);
            client.__dailyfixmeChatListenerCleanup?.();
            try {
                client.stopClient?.();
            } catch (err) {
                if (err.name === "AbortError") {
                    console.log("Logout aborted (expected behavior).");
                } else {
                    console.error("Logout error:", err);
                }
            }
            client.removeAllListeners();
            clearMatrixClientSession();
            console.log("[Matrix] Client stopped; device storage preserved.");
        }

    } catch (err) {
        console.error("[Matrix] Client shutdown failed:", err);
    }
}

let cryptoInitPromise = null;
let cryptoInitSessionKey = null;

export const prepareUserCryptoInBackground = (client, passphrase, options) => {
    const crypto = client?.getCrypto();
    const cryptoOptions = options || {};
    if (!crypto) {
        return Promise.resolve(false);
    }

    const sessionKey = `${client.getUserId?.() || ""}:${client.getDeviceId?.() || ""}`;
    if (!cryptoOptions.force && cryptoInitSessionKey === sessionKey && cryptoInitPromise) {
        return cryptoInitPromise;
    }

    cryptoInitSessionKey = sessionKey;
    cryptoInitPromise = runCryptoInit(client, passphrase, cryptoOptions);

    return cryptoInitPromise;
};

export const prepareRoomEncryptionForSend = async (client, room, expectedUserIds) => {
    const crypto = client?.getCrypto?.();
    if (!crypto || !room) {
        throw new Error("Room encryption is not ready. Please try again.");
    }

    try {
        crypto.setTrustCrossSignedDevices?.(true);

        const expectedRecipients = new Set((expectedUserIds || []).filter(Boolean));
        for (let attempt = 0; attempt < ROOM_READY_ATTEMPT_LIMIT; attempt += 1) {
            const currentRoom = client.getRoom(room.roomId) || room;
            const joinedUserIds = currentRoom.getJoinedMembers()
                .map(member => member.userId)
                .filter(Boolean);
            const joinedUserIdSet = new Set(joinedUserIds);
            const allExpectedRecipientsJoined = [...expectedRecipients]
                .every(userId => joinedUserIdSet.has(userId));

            if (allExpectedRecipientsJoined && joinedUserIds.length > 0) {
                const membershipSnapshot = [...joinedUserIdSet].sort().join("|");
                await crypto.getUserDeviceInfo([...joinedUserIdSet], true);
                const refreshedMembershipSnapshot = currentRoom.getJoinedMembers()
                    .map(member => member.userId)
                    .filter(Boolean)
                    .sort()
                    .join("|");
                if (membershipSnapshot === refreshedMembershipSnapshot) {
                    return;
                }
            }

            await new Promise(resolve => setTimeout(resolve, ROOM_READY_RETRY_DELAY_MS));
        }
        throw new Error("Room membership or recipient devices did not finish syncing.");
    } catch (err) {
        console.error("[Matrix] Failed to prepare recipient devices before sending:", err);
        throw new Error(err.message || "Recipient encryption keys are still syncing. Please try again.");
    }
};

async function runCryptoInit(client, passphrase, options) {
    try {
        const crypto = client.getCrypto();

        console.log("[Matrix] Background crypto setup started");

        crypto.setTrustCrossSignedDevices?.(true);

        if (passphrase) {
            try {
                await crypto.bootstrapSecretStorage({
                    createSecretStorageKey: () =>
                        crypto.createRecoveryKeyFromPassphrase(passphrase),
                    setupNewKeyBackup: !!options.setupNewKeyBackup,
                    setupNewSecretStorage: !!options.setupNewSecretStorage,
                });
            } catch (err) {
                if (!options.allowSecretStorageFailure) {
                    throw err;
                }
                console.warn("[Matrix] Secret storage setup was skipped after backup restore:", err);
            }
        }

        let isCrossSigningReady = false;
        try {
            isCrossSigningReady = await crypto.isCrossSigningReady();
        } catch (err) {
            console.warn("[Matrix] Cross-signing check failed:", err);
        }

        if (!isCrossSigningReady) {
            try {
                await crypto.bootstrapCrossSigning({
                    authUploadDeviceSigningKeys: (makeRequest) => makeRequest(),
                });
            } catch (err) {
                if (!options.allowSecretStorageFailure) {
                    throw err;
                }
                console.warn("[Matrix] Cross-signing bootstrap was skipped after backup restore:", err);
            }
        }

        if (passphrase) {
            try {
                await crypto.loadSessionBackupPrivateKeyFromSecretStorage();
            } catch (err) {
                console.warn("[Matrix] Could not load key backup from secret storage yet:", err);
            }
        }

        try {
            await crypto.checkKeyBackupAndEnable();
        } catch (err) {
            if (!options.allowSecretStorageFailure) {
                throw err;
            }
            console.warn("[Matrix] Key backup enable check failed after backup restore:", err);
        }

        try {
            const isSupported = await crypto.isDehydrationSupported();

            if (isSupported) {
                await crypto.startDehydration({
                    rehydrate: true,
                    createNewKey: true,
                    onlyIfKeyCached: false,
                });
            }
        } catch (err) {
            console.warn("[Matrix] Device dehydration setup failed without blocking chat backup:", err);
        }

        console.log("[Matrix] Background crypto setup complete");
        return true;

    } catch (err) {
        console.error("[Matrix] Crypto setup failed:", err);
        cryptoInitPromise = null;
        cryptoInitSessionKey = null;
        return false;
    }
}
